import { Injectable, inject } from '@angular/core';
import { FolderDto, GalleryDto, TenantDto } from '@nusign/picflow-backend-types';
import { firstValueFrom } from 'rxjs';
import { AppConfigService } from '../providers/config.service';
import { BrowserService } from './browser.service';
import { CustomDomainService } from './custom-domain.service';
import { GalleryService } from './gallery.service';
import { StateService } from './state.service';

@Injectable({
  providedIn: 'root',
})
export class ShareService {
  private browserService = inject(BrowserService);
  private state = inject(StateService);
  private config = inject(AppConfigService);
  private customDomainService = inject(CustomDomainService);
  private galleryService = inject(GalleryService);

  private async getTenantPath(gallery: GalleryDto) {
    const tenantPath =
      this.state.visitingTenant?.path || (gallery?.tenant as TenantDto)?.path || this.state.activeTenant$?.value?.path;

    if (tenantPath) return tenantPath;

    const { tenant } = <GalleryDto & { tenant: TenantDto }>(
      await firstValueFrom(this.galleryService.getGallery(gallery.id))
    );
    return tenant?.path;
  }

  public async getShareDomain(gallery?: GalleryDto, isFolder?: boolean, forceSubdomain?: boolean) {
    let customDomain = !forceSubdomain && this.browserService.getTenantCustomDomain();
    if (!customDomain) {
      if (this.state.activeTenant$?.value) {
        // For your own gallery, the tenant is not requested so won't be there.
        // The second part (checking if the tenant path matches) is there just in case we change this at some point
        const galleryTenantId = (gallery?.tenant as TenantDto)?.id || (gallery?.tenant as string);
        if (galleryTenantId === this.state.activeTenant$.value.id) {
          const customDomainData = await this.customDomainService.getCustomDomain();
          if (customDomainData?.status === 'ready') customDomain = customDomainData.domain;
        }
      }
    }

    if (this.config.env === 'local') {
      if (isFolder) return `${this.config.origin}`;
      return `${this.config.origin}`;
    }

    if (customDomain) return `https://${customDomain}`;

    const tenantPath = await this.getTenantPath(gallery);
    if (tenantPath) {
      const picflowUrlBase = `${this.config.env === 'production' ? '' : this.config.env + '.'}picflow.com`;
      return `https://${tenantPath}.${picflowUrlBase}`;
    }

    return `${this.browserService.getCorrectPicflowUrl()}/gallery`;
  }

  public async getShareLink(
    gallery: GalleryDto,
    options: {
      assetId?: string;
      forceSubdomain?: boolean;
      pwaLink?: boolean;
    } = {}
  ): Promise<{ url: string; post: string; pre: string }> {
    const domain = await this.getShareDomain(gallery, false, options.forceSubdomain);
    let url = `${domain}`;

    if (gallery.parent_gallery_id) {
      const parentGallery = await firstValueFrom(this.galleryService.getGallery(gallery.parent_gallery_id));
      url += `/${parentGallery.path}`;
    }

    url += `/${gallery.path}`;

    if (options.pwaLink) {
      url += '/pwa';
    }

    if (options.assetId) {
      url += `/${options.assetId}`;
    }

    if (this.config.env === 'local') {
      const tenantPath = await this.getTenantPath(gallery);
      url += `?fake-tenant=${tenantPath}`;
    }

    let pre = url.split('://')[1].split('/').slice(0, -1).join('/') + '/';
    const post = url.split('/').pop();

    if (gallery.parent_gallery_id) {
      pre = `.../${pre.split('/').slice(1).join('/')}`;
    }

    return { url, pre, post };
  }

  public async getFolderShareLink(folder: FolderDto) {
    const [gallery] = await firstValueFrom(this.galleryService.getPublicFolderGalleries(folder.id));
    const domain = await this.getShareDomain(gallery, true);

    const pre = `${domain.split('//')[1]}/folder/`;
    const post = folder.path;
    let url = `${domain}/folder/${folder.path}`;

    if (this.config.env === 'local') {
      const tenantPath = await this.getTenantPath(gallery);
      url += `?fake-tenant=${tenantPath}`;
    }

    return { url, pre, post };
  }
}
